import { defineStore } from "pinia";
import { useSharedStore } from "@/entrypoints/stores/shared";
import { AVCrypto } from "@assemblyvoting/av-crypto"
import apiClient from "@/entrypoints/frontend/apiClient";
import { reactive, ref, computed } from "vue";
import type { SignUpVoterParticipation } from "@/types";

export const useSignUpSessionStore = defineStore("signUpSession", () => {
  const sharedStore = useSharedStore();

  // State
  const error = ref<string>(null);
  const privateKey = ref<string>(null);
  const publicKey = ref<string>(null);
  const signature = ref<string>(null);
  const answered = computed<boolean>(()=>{
    return voter.participating !== "unanswered";
  });
  const voter = reactive<SignUpVoterParticipation>({
    name: null,
    participating: "unanswered",
  });

  async function authenticate(electionCodes: string[], curveName: string) {
    error.value = null;

    const avCrypto = new AVCrypto(curveName)
    const keypair = avCrypto.generateProofOfElectionCodes(electionCodes)
    const privateKey = keypair.privateKey;
    const publicKey = keypair.publicKey;
    const signature = avCrypto.sign("", privateKey);

    try {
      const response = await apiClient.post(`${sharedStore.electionUrl}/authenticate_sign_up`, {
        public_key: publicKey,
        signature: signature,
      });

      const data = await response.data;

      startSession(data.voter, {privateKey, publicKey, signature})

    } catch (err: unknown) {
      const apiError = err as { response?: { data: { error: string } } };
      if (apiError.response) error.value = apiError.response.data.error;
    }
  }

  async function submitAnswer(participation?: "yes" | "no") {
    try {
      const response = await apiClient.put(`${sharedStore.electionUrl}/submit_sign_up`, {
        signature: signature.value,
        public_key: publicKey.value,
        participating: participation,
      });

      const data = await response.data;

      voter.participating = data.voter.participating;
    } catch (err: unknown) {
      destroySession();
      const apiError = err as { response?: { data: { error: string } } };
      if (apiError.response) {
        error.value = apiError.response.data.error;
      }
    }
  }

  function destroySession() {
    privateKey.value = null;
    publicKey.value = null;
    signature.value = null;
    voter.name = null;
    voter.participating = "unanswered";
  }

  function startSession(
    { name, participating }: SignUpVoterParticipation,
    { privateKey: pks, publicKey: pkp, signature: sigt }: { privateKey: string; publicKey: string; signature: string }
  ) {
    voter.name = name;
    voter.participating = participating;
    privateKey.value = pks;
    publicKey.value = pkp;
    signature.value = sigt;
  }

  return {
    error,
    answered,
    voter,
    publicKey,
    authenticate,
    submitAnswer,
    destroySession,
  }
});